import { BehaviorSubject } from "rxjs";
import z, { core, output, ZodError, ZodObject, ZodType } from "zod";

export class ValidationContext<
    V extends { [key:string]: any },
    T extends ZodObject<Shape, Config>,
    Shape extends core.$ZodShape,
    Config extends core.$ZodObjectConfig> {

    private formState = new BehaviorSubject(new FormState());
    private schema: T;
    private validatableObject: V;
    private eventListeners: FieldListener[] = [];

    public formState$ = this.formState.asObservable();

    constructor(validationSchema: T, validatableObject: V) {
        this.schema = validationSchema;
        this.validatableObject = validatableObject;
    }

    public setupListeners(element: HTMLElement) {
        Array.from(element.querySelectorAll("input[data-validate-field], select[data-validate-field], textarea[data-validate-field]"))
            .forEach((input) => {
                const field = input.getAttribute("data-validate-field");
                if(!field || !Object.keys(this.schema.shape).includes(field)) { return }

                let listener = input.addEventListener("blur", () => {
                    this.validateOnly(field);
                });
                this.eventListeners.push(new FieldListener(field, listener, 'blur'));

                listener = input.addEventListener("input", () => {
                    if(this.isInvalid(field) || input.getAttribute("type") === "checkbox") {
                        this.validateOnly(field);
                    }
                });
                this.eventListeners.push(new FieldListener(field, listener, 'input'));
            });
    }

    private parseZodErrors(zodError: ZodError<output<T>>) : Record<string, string[]> {
        const errors: Record<string, string[]> = {};

        //return z.flattenError(zodError).fieldErrors as Record<string, string[]>;

        zodError.issues.forEach((issue) => {
            const fieldPath = issue.path.join('.');
            if (!errors[fieldPath]) {
                errors[fieldPath] = [];
            }
            errors[fieldPath].push(issue.message);
        });

        return errors;
    }

    private parseZodSuccesses(zodError: ZodError<output<T>>) : Record<string,boolean> {
        const successes: Record<string, boolean> = {};
        Object.keys(this.schema).forEach((fieldPath) => {
            successes[fieldPath] = true;
        });

        zodError.issues.forEach((issue) => {
            const fieldPath = issue.path.join('.');
            delete successes[fieldPath];
        });
        return successes;
    }

    public validate(silent: boolean = false) : { outcome?: boolean, successes: Record<string, boolean>, errors: Record<string, string[]> } {
        const result = this.schema.safeParse(this.validatableObject);
        const outcome = result.success && !result.error;
        const successes = result.error ? this.parseZodSuccesses(result.error) : {}
        const errors = result.error ? this.parseZodErrors(result.error) : {}

        if(!silent && result.error) {
            this.formState.next({ errors, successes });
        }

        return {
            outcome: outcome,
            successes: successes,
            errors: errors
        };
    }

    private processZodFieldValidation(field: string) {
        const fieldSchema = this.schema.shape[field] as ZodType ?? null;
        if (!fieldSchema) {
            console.warn(`Validation: No validation schema defined for the field: ${field}`);
            return false;
        }

        const result = fieldSchema.safeParse(this.validatableObject[field]);

        const currentState = this.formState.getValue();

        if (result.success) {
            delete currentState.errors[field];
            currentState.successes[field] = true;
            this.formState.next(currentState);
            return true;
        }

        currentState.errors[field] = [z.treeifyError(result.error).errors[0]];
        currentState.successes[field] = false;
        this.formState.next(currentState);
        return false;
    }

    public isValid(field :string) {
        const currentFormState = this.formState.getValue();
        return currentFormState.successes[field] ?? false;
    }
    public isInvalid(field :string) {
        const currentFormState = this.formState.getValue();
        return Object.keys(currentFormState.errors).includes(field);
    }
    public firstErrorFor(field :string) {
        const currentFormState = this.formState.getValue();
        if (currentFormState.errors[field]) {
            return currentFormState.errors[field][0] ?? null;
        }
        return null;
    }
    public getErrorsFor(field : string) {
        const currentFormState = this.formState.getValue();
        return currentFormState.errors[field] ?? [];
    }
    public allErrors() {
        const currentFormState = this.formState.getValue();
        return currentFormState.errors;
    }
    public hasErrors() {
        const currentFormState = this.formState.getValue();
        return Object.keys(currentFormState.errors).length > 0;
    }
    public allSuccesses() {
        const currentFormState = this.formState.getValue();
        return currentFormState.successes;
    }
    public reset() {
        const currentFormState = this.formState.getValue();
        currentFormState.errors = {} as Record<string, string[]>;
        currentFormState.successes = {} as Record<string, boolean>;
        this.formState.next(currentFormState);
    }
    public validateOnly(field: string) {
        const result = this.processZodFieldValidation(field);
        return result;
    }

}

export class FormState {
    errors: Record<string, string[]> = {};
    successes: Record<string, boolean> = {};
}
class FieldListener {
    field: string;
    listener: void;
    event: string;

    constructor(field: string, listener: void, event: string) {
        this.field = field;
        this.listener = listener;
        this.event = event;
    }
}